import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { environment } from '@env/environment';
import { PermissionService } from '@core/services/permission.service';
import { TranslationService } from '@core/services/translation.service';
import { showToast } from '@core/store/modal.actions';

// Route guard factory. Blocks navigation to a create/edit page when the user's
// role lacks the given ability on the module. Pairs with the server-side
// check.permission middleware so URL-nav can't bypass the hidden "New" button.
//
// In demo mode (environment.demoAuth === true) the guard always allows
// navigation — same escape hatch as authGuard — so dev / UI-only demos aren't
// blocked by a missing role matrix.
export function permissionGuard(
  module: string,
  ability: 'view' | 'edit' | 'remove' = 'edit',
): CanActivateFn {
  return () => {
    if (environment.demoAuth) return true;

    const permissions = inject(PermissionService);
    const router = inject(Router);
    const store = inject(Store);
    const ts = inject(TranslationService);

    if (permissions.can(module, ability)) return true;

    store.dispatch(
      showToast({
        message: ts.translate('common.toast.permission_denied'),
        tone: 'error',
      }),
    );
    return router.createUrlTree(['/dashboard']);
  };
}
